import type {CourseId, UniqueNumberId, UniqueStringId} from '../types/branded';
import {ONE_HOUR_MS, ONE_WEEK_MS} from '../utils/time';

export type SectionId = UniqueStringId<'SectionId'>;

/**
 * Assumed duration of a section in milliseconds (1 hour).
 * Sections typically run 50-60 minutes.
 */
export const SECTION_DURATION_MS = ONE_HOUR_MS;

/**
 * Length of one section meeting in minutes, as section leaders are told
 * and as calendar events are created. Shorter than a slot, so meetings in
 * adjacent slots leave a break between them.
 */
export const SECTION_MEETING_MINUTES = 50;

/**
 * The week index associated with sections. This is one less than the course
 * week index. The first section of the course is on section week index 1,
 * corresponding to a course week index of 1. This index is frequently used in
 * the sections collection to refer to the week of the section.
 */
export type SectionWeekId = UniqueNumberId<'SectionWeekId'>;

// TODO: [#1484](https://github.com/CodeinPlace/cip/issues/1484) - zoom.ts sectionAttendance / sectionAttendanceIds and useWeekHooks sectionWeek indexing clash
/**
 * The week index associated with sections. This is one less than the course
 * week index. The first section of the course is on section week index 0,
 * corresponding to a course week index of 1. This index is frequently used in
 * the sections collection to refer to the week of the section.
 */
export type SectionWeekIdx = UniqueNumberId<'SectionWeekIdx'>;

/**
 * The week index associated with the course. This is one more than the section
 * week index. The first section of the course is on section week index 0,
 * corresponding to a course week index of 1.
 */
export type CourseWeekId = UniqueNumberId<'CourseWeekId'>;

/**
 * Parses and validates a value as a SectionId.
 * Validates: non-empty string, no slash (malformed path indicator).
 */
export function parseSectionId(value: unknown): SectionId | undefined {
  if (typeof value !== 'string' || value === '' || value.includes('/')) {
    return undefined;
  }
  return value as SectionId;
}

/**
 * Parses and validates a value as a CourseId.
 * Validates: non-empty string, no slash (malformed path indicator).
 *
 * @param value - The value to validate (e.g. a Cloud Function path param).
 * @returns The branded CourseId, or undefined if not a valid course id.
 */
export function parseCourseId(value: unknown): CourseId | undefined {
  if (typeof value !== 'string' || value === '' || value.includes('/')) {
    return undefined;
  }
  return value;
}

/**
 * Parses and validates a value as a SectionWeekId.
 * Validates: integer >= 1.
 */
export function parseSectionWeekId(value: unknown): SectionWeekId | undefined {
  const num = Number(value);
  if (isNaN(num) || !Number.isInteger(num) || num < 1) {
    return undefined;
  }
  return num as SectionWeekId;
}

/**
 * Parses and validates a value as a SectionWeekId.
 * Validates: integer >= 1.
 */
export function parseSectionWeekIdx(
  value: unknown,
): SectionWeekIdx | undefined {
  const num = Number(value);
  if (isNaN(num) || !Number.isInteger(num) || num < 0) {
    return undefined;
  }
  return num as SectionWeekIdx;
}

/**
 * Returns the section date for a given week under the sticky-UTC
 * policy: sections are fixed UTC instants, and Week-N is exactly
 * Week-0 plus `weekIndex * ONE_WEEK_MS`.
 *
 * @deprecated Prefer {@link computeSectionInstant} in new code — it
 *   composes `timeIndex` and `weekIndex` in one call and makes the
 *   sticky-UTC contract explicit in its name. This function is kept
 *   as a transitional wrapper so existing callers continue to compile
 *   during the migration.
 *
 *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
 *   migrate remaining callers to `computeSectionInstant` and delete
 *   this wrapper.
 *
 * Historical note: earlier revisions used `setUTCDate` + `setHours`
 * (PR #1975, Mar 2026) and later just `setDate` (an in-flight fix
 * reverted by the PR that added `computeSectionInstant`). Both forms
 * silently took `process.env.TZ` as an implicit parameter via
 * `Date.prototype.getHours`/`getDate` and produced different output
 * on browser (user-local tz) vs Cloud Function (UTC), which is how
 * the Egypt DST regression surfaced the client/server divergence.
 * Do not reintroduce local-tz `Date` methods here — see the policy
 * block in `slotTime.ts` for the long version.
 *
 * @param firstSectionDate - The anchor instant for the slot, already
 *   offset by any applicable `timeIndex` at call time.
 * @param weekIndex - Number of weeks to advance. 0 returns a clone of
 *   `firstSectionDate`. Negative values are supported.
 * @returns A new `Date` exactly `weekIndex * ONE_WEEK_MS` after
 *   `firstSectionDate`. Output is ambient-tz-independent.
 */
export function getSectionDateForWeek(
  firstSectionDate: Date,
  weekIndex: number,
): Date {
  return new Date(firstSectionDate.getTime() + weekIndex * ONE_WEEK_MS);
}

/**
 * Returns the Week-0 section instant for the given
 * `sectionTimeIndex`. Equivalent to
 * `computeSectionInstant({firstSectionTimestamp: courseFirstSectionTimestamp, timeIndex: sectionTimeIndex, weekIndex: 0})`
 * — with two legacy quirks preserved for caller compatibility:
 * `sectionTimeIndex` may be the string `'none'` or `null`, both of
 * which collapse to `timeIndex: 0`; and the return value is the
 * anchor-as-Date for `timeIndex: 0`, not the "first section week".
 *
 * @deprecated Prefer {@link computeSectionInstant} directly — callers
 *   that need the `'none' | null` collapse can coerce at the call
 *   site.
 *
 *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
 *   migrate remaining callers and delete this helper.
 *
 * @param courseFirstSectionTimestamp - ISO 8601 course anchor.
 * @param sectionTimeIndex - Hour offset; `'none'` and `null` are
 *   treated as 0.
 * @returns The anchor instant plus `sectionTimeIndex` hours.
 */
export function getFirstSectionDateTime({
  courseFirstSectionTimestamp,
  sectionTimeIndex,
}: {
  courseFirstSectionTimestamp: string;
  sectionTimeIndex: number | 'none' | null;
}): Date {
  const firstSectionDateTime = new Date(courseFirstSectionTimestamp);
  const timeIndexNumber =
    typeof sectionTimeIndex === 'number' ? sectionTimeIndex : 0;
  if (timeIndexNumber !== 0) {
    const updatedFirstSectionDateTime = new Date(
      firstSectionDateTime.getTime() + timeIndexNumber * ONE_HOUR_MS,
    );
    firstSectionDateTime.setTime(updatedFirstSectionDateTime.getTime());
  }
  return firstSectionDateTime;
}
